import { createIdentity, type Credentials } from "@alice-and-bot/core";
import { i, init, lookup } from "@instantdb/react";
import { useEffect, useState } from "preact/hooks";
import {
  computeMessageCount,
  computeMessageLimit,
  computeQuota,
  computeQuotaLeft,
  isProFromEvents,
  latestPaidEventTimestamp,
  monthInMs,
  nextCreditReturnMsSince,
  type PaymentEvent,
  totalPaidAmount,
  usageWindowStart,
} from "../../shared/quota.ts";
import "./style.css";

const schema = i.schema({
  rooms: {},
  links: {
    apiTokenOwner: {
      forward: { on: "apiTokens", has: "one", label: "owner" },
      reverse: { on: "users", has: "many", label: "apiTokens" },
    },
    paymentEventOwner: {
      forward: { on: "paymentEvents", has: "one", label: "owner" },
      reverse: { on: "users", has: "many", label: "paymentEvents" },
    },
  },
  entities: {
    videoResults: i.entity({
      user: i.string().indexed(),
      time: i.number().indexed(),
      query: i.string().optional(),
    }),
    outOfSync: i.entity({
      user: i.string(),
      videoHash: i.string(),
      srtHash: i.string(),
    }),
    users: i.entity({
      aliceAndBotPrivateEncryptKey: i.string().optional(),
      aliceAndBotPrivateSignKey: i.string().optional(),
      aliceAndBotPublicSignKey: i.string().indexed().optional(),
      emailResults: i.boolean().optional(),
      lastVisit: i.number().indexed().optional(),
      messageCount: i.number().optional(),
      messageCountSince: i.number().optional(),
      unsubscribed: i.boolean().indexed().optional(),
      userId: i.string().indexed(),
    }),
    apiTokens: i.entity({
      seed: i.string().indexed(),
      createdAt: i.number(),
      active: i.boolean(),
      name: i.string().optional(),
    }),
    paymentEvents: i.entity({
      userId: i.string().indexed(),
      type: i.string().indexed(),
      amountUSD: i.number(),
      timestamp: i.number().indexed(),
    }),
    deletedAccountUsage: i.entity({
      userHash: i.string().indexed(),
      time: i.number().indexed(),
    }),
  },
});

const db = init({
  devtool: false,
  schema,
  appId: "8b15b510-662a-46bf-bd97-321c6da0136f",
});

export const signInWithToken = (token: string) =>
  db.auth.signInWithToken(token);

const { useQuery, transact, tx } = db;

const getUserDbEntry = (userId: string) => {
  const { data, isLoading } = useQuery({
    users: { $: { where: { userId } }, paymentEvents: {} },
  });
  return {
    user: data?.users?.length ? data.users[0] : null,
    isLoading,
  };
};

const getUserPaymentEvents = (user: string): PaymentEvent[] => {
  const { user: result } = getUserDbEntry(user);
  if (!result) return [];
  return ((result as any).paymentEvents ?? []) as PaymentEvent[];
};

const allUsageTimes = (user: string): number[] => {
  const { data } = useQuery({ videoResults: { $: { where: { user } } } });
  return data?.videoResults?.map(({ time }: { time: number }) => time) ?? [];
};

export const quotaLeft = (user: string): number => {
  const events = getUserPaymentEvents(user);
  return computeQuotaLeft(events, allUsageTimes(user));
};

export const nextCreditReturn = (user: string): number | null => {
  const events = getUserPaymentEvents(user);
  return nextCreditReturnMsSince(
    allUsageTimes(user),
    computeQuota(events),
    usageWindowStart(events),
  );
};

export const totalQuota = (user: string): number => {
  const events = getUserPaymentEvents(user);
  return computeQuota(events);
};

export type UsageEvent = {
  id: string;
  time: number;
  user: string;
  query?: string;
};

export type PaymentHistoryEvent = {
  id: string;
  userId: string;
  type: string;
  amountUSD: number;
  timestamp: number;
};

export const useUsageHistory = (
  user: string | null | undefined,
): {
  downloads: UsageEvent[];
  isLoading: boolean;
} => {
  const { data, isLoading } = useQuery(
    user
      ? { videoResults: { $: { where: { user } } } }
      : { videoResults: { $: { where: { user: "" } } } },
  );
  const raw = (data?.videoResults ?? []) as UsageEvent[];
  const downloads = [...raw].sort((a, b) => b.time - a.time);
  return { downloads, isLoading };
};

export const usePaymentHistory = (
  user: string | null | undefined,
): {
  payments: PaymentHistoryEvent[];
  isLoading: boolean;
} => {
  const { data, isLoading } = useQuery(
    user
      ? { users: { $: { where: { userId: user } }, paymentEvents: {} } }
      : { users: { $: { where: { userId: "" } }, paymentEvents: {} } },
  );
  const userObj = data?.users?.[0];
  const raw = ((userObj as any)?.paymentEvents ?? []) as PaymentHistoryEvent[];
  const payments = [...raw].sort((a, b) => b.timestamp - a.timestamp);
  return { payments, isLoading };
};

export const messageStats = (
  userEmail: string,
): {
  used: number;
  limit: number;
  remaining: number;
  resetMs: number | null;
} => {
  const { user } = getUserDbEntry(userEmail);
  const events = getUserPaymentEvents(userEmail);
  const limit = computeMessageLimit(events);
  const now = Date.now();
  const since = (user as any)?.messageCountSince ?? 0;
  const used = computeMessageCount(
    (user as any)?.messageCount ?? 0,
    since,
    events,
    now,
  );
  const remaining = Math.max(0, limit - used);
  const paidTimestamp = latestPaidEventTimestamp(events);
  const windowStart = paidTimestamp > 0 ? paidTimestamp : since;
  const resetMs = windowStart > 0
    ? Math.max(0, windowStart + monthInMs - now)
    : null;
  return { used, limit, remaining, resetMs };
};

export const postApi = <T>(endpoint: string, body: T) =>
  fetch(`https://api.find-scene.com/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((response) => {
    if (response.status !== 200) throw new Error("failed api request");
    return response.text();
  });

export const authUrl = () =>
  db.auth.createAuthorizationURL({
    clientName: "google-web",
    redirectURL: `${globalThis.location.origin}/app`,
  });

export const useAuth = () => db.useAuth();

export const signOut = () => db.auth.signOut();

export const sendMagicCode = (email: string) =>
  db.auth.sendMagicCode({ email });

export const signInWithMagicCode = (
  { email, code }: { email: string; code: string },
) => db.auth.signInWithMagicCode({ email, code });

export type { Credentials };

const isCompleteCredentials = (
  value: {
    aliceAndBotPublicSignKey?: string;
    aliceAndBotPrivateSignKey?: string;
    aliceAndBotPrivateEncryptKey?: string;
  } | null,
): value is {
  aliceAndBotPublicSignKey: string;
  aliceAndBotPrivateSignKey: string;
  aliceAndBotPrivateEncryptKey: string;
} =>
  !!(
    value?.aliceAndBotPublicSignKey &&
    value?.aliceAndBotPrivateSignKey &&
    value?.aliceAndBotPrivateEncryptKey
  );

const pendingCreations = new Set<string>();

export const useAliceAndBotCredentials = (
  userEmail: string | undefined,
): Credentials | null => {
  const { user: userEntity, isLoading: userLoading } = userEmail
    ? getUserDbEntry(userEmail)
    : { user: null, isLoading: false };
  const [locked, setLocked] = useState(false);
  const hasCompleteAliceAndBotCredentials = isCompleteCredentials(userEntity);
  const existing = hasCompleteAliceAndBotCredentials
    ? {
      publicSignKey: userEntity.aliceAndBotPublicSignKey,
      privateSignKey: userEntity.aliceAndBotPrivateSignKey,
      privateEncryptKey: userEntity.aliceAndBotPrivateEncryptKey,
    }
    : undefined;

  useEffect(() => {
    if (
      !userEmail || locked || hasCompleteAliceAndBotCredentials || userLoading
    ) {
      return;
    }
    if (!userEntity) {
      if (pendingCreations.has(userEmail)) return;
      pendingCreations.add(userEmail);
      setLocked(true);
      transact([
        tx.users[lookup("userId", userEmail)].update({
          lastVisit: Date.now(),
        }),
      ])
        .catch(() => {})
        .finally(() => {
          pendingCreations.delete(userEmail);
          setLocked(false);
        });
      return;
    }
    setLocked(true);
    createIdentity(userEmail)
      .then((credentials: Credentials) =>
        transact([
          tx.users[userEntity.id].update({
            aliceAndBotPublicSignKey: credentials.publicSignKey,
            aliceAndBotPrivateSignKey: credentials.privateSignKey,
            aliceAndBotPrivateEncryptKey: credentials.privateEncryptKey,
          }),
        ])
      )
      .catch((e: unknown) =>
        console.error("Failed to create alice-and-bot credentials:", e)
      )
      .finally(() => setLocked(false));
  }, [
    userEmail,
    userEntity?.id,
    hasCompleteAliceAndBotCredentials,
    locked,
    userLoading,
  ]);

  useEffect(() => {
    if (userEntity?.id) {
      transact([tx.users[userEntity.id].update({ lastVisit: Date.now() })])
        .catch(() => {});
    }
  }, [userEntity?.id]);

  return existing ?? null;
};

export const useUserSettings = (userId: string) => {
  const { data } = useQuery({
    users: { $: { where: { userId } }, apiTokens: {}, paymentEvents: {} },
  });
  return data?.users?.length ? data.users[0] : null;
};

export const updateUserPref =
  (entityId: string) => (field: string, value: boolean) =>
    transact([tx.users[entityId].update({ [field]: value })]);

export const revokeToken = (tokenId: string) =>
  transact([tx.apiTokens[tokenId].update({ active: false })]);

export const generateApiToken = async (
  email: string,
  refreshToken: string,
  name: string,
): Promise<string> => {
  const response = await fetch(
    "https://api.find-scene.com/api/generate-token",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken, name }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to generate token: ${response.status}`);
  }
  const { token } = await response.json();
  return token as string;
};

export const deleteAccount = async (
  email: string,
  refreshToken: string,
): Promise<void> => {
  const response = await fetch(
    "https://api.find-scene.com/api/delete-account",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to delete account: ${response.status}`);
  }
};

export const createCheckoutSession = async (
  email: string,
  refreshToken: string,
  priceId: string,
  metadata?: Record<string, string>,
): Promise<string> => {
  const response = await fetch(
    "https://api.find-scene.com/api/create-checkout-session",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken, priceId, metadata }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to create checkout session: ${response.status}`);
  }
  const { url } = await response.json();
  return url as string;
};

export const createPortalSession = async (
  email: string,
  refreshToken: string,
): Promise<string> => {
  const response = await fetch(
    "https://api.find-scene.com/api/create-portal-session",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to create portal session: ${response.status}`);
  }
  const { url } = await response.json();
  return url as string;
};

export const syncCheckoutSession = async (
  email: string,
  refreshToken: string,
  sessionId: string,
): Promise<boolean> => {
  const response = await fetch(
    "https://api.find-scene.com/api/sync-checkout-session",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken, sessionId }),
    },
  );
  if (!response.ok) return false;
  const { success } = await response.json();
  return Boolean(success);
};

export const syncUserSubscription = async (
  email: string,
  refreshToken: string,
): Promise<boolean> => {
  const response = await fetch(
    "https://api.find-scene.com/api/sync-active-subscription",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken }),
    },
  );
  if (!response.ok) return false;
  const { synced } = await response.json();
  return Boolean(synced);
};

export const useUserIsPro = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const user = useUserSettings(email);
  if (!user) return false;
  const events = ((user as any).paymentEvents ?? []) as PaymentEvent[];
  return isProFromEvents(events);
};

export const useUserCurrentTierPrice = (
  email: string | null | undefined,
): number => {
  if (!email) return 0;
  const user = useUserSettings(email);
  if (!user) return 0;
  const events = ((user as any).paymentEvents ?? []) as PaymentEvent[];
  return totalPaidAmount(events);
};

export const disconnectTwitterAccount = async (
  email: string,
  refreshToken: string,
): Promise<boolean> => {
  const response = await fetch(
    "https://api.find-scene.com/api/auth/twitter/disconnect",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken }),
    },
  );
  return response.ok;
};

export const claimTwitterHandle = async (
  email: string,
  refreshToken: string,
  claim: { id: string; handle: string; sig: string },
): Promise<{ ok: boolean; handle: string; id: string }> => {
  const response = await fetch(
    "https://api.find-scene.com/api/auth/twitter/claim",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        refreshToken,
        twitterUserId: claim.id,
        twitterHandle: claim.handle,
        sig: claim.sig,
      }),
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to claim handle: ${response.status}`);
  }
  return response.json();
};

export const linkTwitterHandle = async (
  email: string,
  refreshToken: string,
  handle: string,
): Promise<{ ok: boolean; handle: string; id: string }> => {
  const response = await fetch(
    "https://api.find-scene.com/api/auth/twitter/link-handle",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, refreshToken, handle }),
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to link handle: ${response.status}`);
  }
  return response.json();
};
